import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import moment from 'moment';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { SubSink } from 'subsink';
import { ServiceInput } from '../../../../core/models/service-input.model';
import { RequestViewService } from '../../../../core/services/request-view.service';

@Component({
    selector: 'app-time-range-input',
    templateUrl: './time-range-input.component.html',
    styleUrls: ['./time-range-input.component.css']
})
export class TimeRangeInputComponent implements OnInit, OnDestroy {
    @Input() startTimeInput: ServiceInput;
    @Input() endTimeInput: ServiceInput;
    @Input() startDateInput: ServiceInput;
    @Input() endDateInput: ServiceInput;
    @Input() serviceInputsForm: FormGroup;
    @Input() readOnly: boolean;
    @Output() onChange = new EventEmitter<any>();
    
    timeInputRequiredMsg = '';
    form;
    
    // 24-hour timepicker theme
    timepickerTheme: NgxMaterialTimepickerTheme = {
        container: {
            bodyBackgroundColor: '#fff',
            buttonColor: '#4f008c'
        },
        dial: {
            dialBackgroundColor: '#4f008c',
        },
        clockFace: {
            clockFaceBackgroundColor: '#f0f0f0',
            clockHandColor: '#4f008c',
            clockFaceTimeInactiveColor: '#666'
        }
    };
    
    subs = new SubSink();

    constructor(private requestViewService: RequestViewService) {}

    ngOnInit(): void {
        this.form = this.serviceInputsForm.get('requiredFormGroup');
        this.subscribeOnDateChange();
    }

    subscribeOnDateChange() {
        this.subs.sink = this.requestViewService.dateRangeChanged.subscribe(() => {
            this.handleTimeValidationOnDate();
        });
    }

    onTimeRangeChange() {
        const startTime = this.form.get(this.startTimeInput.controlName).value;
        const endTime = this.form.get(this.endTimeInput.controlName).value;

        if (startTime && !endTime) {
            this.timeInputRequiredMsg = 'pleaseEnterEndTime';
            this.form.get(this.endTimeInput.controlName).setErrors({ startOrEndTimeIsRequired: true });
        } else if (endTime && !startTime) {
            this.timeInputRequiredMsg = 'pleaseEnterStartTime';
            this.form.get(this.endTimeInput.controlName).setErrors({ startOrEndTimeIsRequired: true });
        } else if (startTime && endTime) {
            this.form.get(this.endTimeInput.controlName).setErrors(null);
            this.handleTimeValidationOnDate();
        }

        this.onChange.emit(true);
    }

    handleTimeValidationOnDate() {
        const startTime = this.form.get(this.startTimeInput.controlName).value;
        const endTime = this.form.get(this.endTimeInput.controlName).value;
        
        if (startTime && endTime) {
            const startDate = this.form.get(this.startDateInput.controlName).value;
            const endDate = this.form.get(this.endDateInput.controlName).value;

            if (startDate && endDate) {
                const startDateAndTime = this.setDateAndTime(startDate, startTime);
                const endDateAndTime = this.setDateAndTime(endDate, endTime);
                const timestampIsSameOrAfter = moment(endDateAndTime).isSameOrAfter(moment(startDateAndTime));

                if (!timestampIsSameOrAfter) {
                    this.form.get(this.endTimeInput.controlName).setErrors({ endTimeBeforeStartTimeError: true });
                } else {
                    const errors = this.form.get(this.endTimeInput.controlName).errors;
                    if (errors) {
                        delete errors['endTimeBeforeStartTimeError'];
                        if (Object.keys(errors).length === 0) {
                            this.form.get(this.endTimeInput.controlName).setErrors(null);
                        }
                    }
                }
            } else {
                // Validate time-only scenario
                const start = moment(startTime, 'HH:mm');
                const end = moment(endTime, 'HH:mm');
                
                if (!end.isSameOrAfter(start)) {
                    this.form.get(this.endTimeInput.controlName).setErrors({ endTimeBeforeStartTimeError: true });
                }
            }
        }
    }

    setDateAndTime(dateStr, timeIn24hFormat) {
        let date = moment(dateStr);
        let time = moment(timeIn24hFormat, 'HH:mm');

        date.set({
            hour: time.get('hour'),
            minute: time.get('minute'),
            second: 0,
            millisecond: 0
        });
        return date;
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}